import React, { useMemo, useState, useEffect } from 'react';
import { Button, Switch, Collapse } from '@mui/material';
import { CircularProgress, DialogTitle, DialogContent, Avatar, List, ListItem, Radio } from '@mui/material';
import { ListItemIcon, TextField, RadioGroup, FormControlLabel, FormLabel, FormControl } from '@mui/material';
import { ListItemText, Grid, Chip, Typography } from '@mui/material';
import { MailOutline, Phone, WorkOutline, EditRounded, AddRounded, HighlightOffRounded } from '@mui/icons-material';
import { CloseRounded, RotateLeftRounded, DeleteOutlineOutlined, AccessibilityRounded } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { RestorePageRounded, TransferWithinAStationRounded, LocalShippingRounded } from '@mui/icons-material';
import { EmailRounded, PriorityHighRounded, CheckCircleRounded, CancelRounded } from '@mui/icons-material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { GeneralButton, RoundedDialog, StandardDialogActions, TooltipIconButton } from '../../../Global/Components';
import { HeaderButton } from '../../../Global/Components';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '../../../../constants';
import { getCompany, fetchGroups } from '../../redux/actions';
import { updateTour } from '../../../Profile/redux/actions';
import { options } from '../../constants';
import Tours from '../../../Global/Assistant/Tours';
import GroupCell from '../../components/GroupCell';
import { CASES } from '../../../Global/Assistant/Tours/constants';
import PageHeader from '../../../Global/PageHeader';
import { grey, red, blue, green } from '@mui/material/colors';
import StateManager from '../../../Global/StateManager';
import OptionsInput from '../../../Global/DataGrid/Filters/OptionsInput';
import { Alert, AlertTitle } from '@mui/material';
import { FormatDate, validateEmail } from '../../../Global/Functions';
import { isArray, isEmpty } from 'lodash';
import axios from 'axios';
import moment from 'moment';
import { TransferWorkDialog, WorkTransfer } from '../../../Profile';
import ImportUsersDialog from '../../components/ImportUsersDialog';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { useDataGridPagination } from '../../../Global/hooks';
import NewGroupDialog from '../../components/NewGroupDialog';
import GroupsDialog from '../../components/GroupsDialog';

import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const DEFAULT_PAGE_SIZE = 100;

export default function Users() {
  let query = useQuery();
  const isTour = query.get('start_tour');
  const dispatch = useDispatch();
  const { company, groups } = useSelector(({ admin }) => admin);
  const currentUser = useSelector(({ profile }) => profile).user;

  const [selected, setSelected] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [signupDialog, setSignupDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupsDialog, setGroupsDialog] = useState(false);
  const [accessCounts, setAccessCounts] = useState([]);
  const apiRef = useGridApiRef();
  useEffect(() => {
    loadAccessCounts();
  }, []);

  function loadAccessCounts() {
    axios
      .get('/admin/management/getUsersPageInfo')
      .then(({ data }) => {
        setAccessCounts(data.accessCounts || []);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  const columns = useMemo(() => {
    return [
      {
        field: 'avatar',
        headerName: '',
        width: 20,
        filterable: false,
        display: 'flex',
        renderCell: (params) => (
          <Avatar
            src={params.row.suspendedAt ? undefined : params.row?.avatar}
            style={{
              background: params.row.suspendedAt ? undefined : params.row?.avatarColor,
              width: 40,
              height: 40,
            }}
          >
            {params.row?.avatarLetters}
          </Avatar>
        ),
      },
      { field: 'fullName', headerName: 'Name', width: 200, display: 'flex' },
      { field: 'email', headerName: 'Email', width: 200, display: 'flex' },
      { field: 'mobile', headerName: 'Mobile', width: 200, display: 'flex' },
      { field: 'jobRole', headerName: 'Role', width: 200, display: 'flex' },
      {
        field: 'groups',
        headerName: 'Groups',
        width: 200,
        display: 'flex',
        renderCell: (params) => <GroupCell groups={params.value} />,
        filterOperators: [
          {
            label: 'Includes',
            value: 'includes',
            InputComponent: OptionsInput,
            InputComponentProps: {
              options: groups?.map((item) => {
                return { title: item.name, _id: item._id };
              }),
            },
          },
        ],
      },
      {
        field: 'access',
        headerName: 'Access',
        width: 200,
        display: 'flex',
        renderCell: (params) => {
          if (params.row?.suspendedAt) return <Chip color="error" label="Suspended" />;

          let resolved = options.find((x) => x.value === params.value);
          if (!resolved) return '-';
          return (
            <Grid container alignItems="center">
              {resolved.icon}
              <span style={{ marginLeft: 8 }}>{resolved.message}</span>
            </Grid>
          );
        },
        filterOperators: [
          {
            label: 'Includes',
            value: 'includes',
            InputComponent: OptionsInput,
            InputComponentProps: {
              options: options?.map((item) => {
                return { title: item.message, _id: item.value };
              }),
            },
          },
        ],
      },
      {
        field: 'microsoftId',
        headerName: 'SSO',
        width: 200,
        type: 'string',
        display: 'flex',
        renderCell: (params) => {
          return (
            <TooltipIconButton text={Boolean(params.value) ? 'SSO account connected' : 'SSO not setup'}>
              {Boolean(params.value) ? <CheckCircleRounded color="success" /> : <CancelRounded color="error" />}
            </TooltipIconButton>
          );
        },
        filterOperators: [
          {
            label: 'Is connected',
            value: 'isNotEmpty',
          },
          {
            label: 'Is not setup',
            value: 'isEmpty',
          },
        ],
        valueFormatter: '',
      },
      {
        field: 'ssoMandatory',
        headerName: 'SSO Enforcement',
        width: 200,
        type: 'string',
        display: 'flex',
        renderCell: (params) => {
          const mandatory = Boolean(params.row.ssoMandatory);
          let enforced = false;
          const id = params.row?._id;
          const groups = params.row?.groups;
          const includedUsers = params.row?.ssoIncludedUsers || [];
          const includedGroups = params.row?.ssoIncludedGroups || [];
          const excludedUsers = params.row?.ssoExcludedUsers || [];
          const excludedGroups = params.row?.ssoExcludedGroups || [];
          if (!mandatory) {
            if (includedUsers && includedUsers.includes(id)) {
              enforced = true;
            } else if (groups && groups.length > 0) {
              for (let i = 0; i < groups.length; i++) {
                if (includedGroups.includes(groups[i]._id)) {
                  enforced = true;
                  break;
                }
              }
            }
          }
          if (mandatory) {
            enforced = true;
            if (excludedUsers && excludedUsers.includes(id)) {
              enforced = false;
            } else if (groups && groups.length > 0) {
              for (let i = 0; i < groups.length; i++) {
                if (excludedGroups.includes(groups[i]._id)) {
                  enforced = false;
                  break;
                }
              }
            }
          }
          return (
            <TooltipIconButton text={enforced ? 'SSO enforced' : 'SSO not enforced'}>
              {enforced ? <LockIcon color="success" /> : <LockOpenIcon color="error" />}
            </TooltipIconButton>
          );
        },
        filterable: false,
        valueFormatter: '',
      },
      {
        field: 'lastSeen',
        headerName: 'Last seen',
        width: 200,
        type: 'date',
        display: 'flex',
        renderCell: (params) => FormatDate(params.value, true),
        valueFormatter: '',
      },
    ];
  }, [groups]);

  useEffect(() => {
    dispatch(getCompany());
    dispatch(fetchGroups());
  }, [dispatch]);

  const tablePaginationConfig = useDataGridPagination(
    'users',
    columns,
    `/admin/management/companyUsersTable`,
    'data',
    'total',
    'personal',
    { defaultPageSize: DEFAULT_PAGE_SIZE, checkboxPosition: 'left' },
  );

  function downloadExcel() {
    const rows = apiRef.current.getRowModels();
    const csvData = [];

    rows.forEach((row) => {
      const {
        access,
        fullName,
        ssoMandatory,
        ssoExcludedUsers,
        ssoExcludedGroups,
        ssoIncludedUsers,
        ssoIncludedGroups,
        groups,
        mobile,
        email,
        jobRole,
        lastSeen,
        sso,
        microsoftId,
      } = row;

      const groupsName = groups && groups[0]?.name ? groups[0]?.name : 'n/a';
      const phoneNumber = mobile ? mobile : 'n/a';
      const eMail = email ? email : 'n/a';
      const jobrole = jobRole ? jobRole : 'n/a';
      const seenAt = lastSeen ? lastSeen : 'never logged in';
      const ssoStatus = sso ?? (Boolean(microsoftId) ? 'SSO account connected' : 'SSO not setup');

      csvData.push({
        access: access ?? 'n/a',
        fullName: fullName ?? 'n/a',
        ssoMandatory: ssoMandatory ?? false,
        ssoExcludedUsers: ssoExcludedUsers ? ssoExcludedUsers.join(', ') : 'n/a',
        ssoExcludedGroups: ssoExcludedGroups ? ssoExcludedGroups.join(', ') : 'n/a',
        ssoIncludedUsers: ssoIncludedUsers ? ssoIncludedUsers.join(', ') : 'n/a',
        ssoIncludedGroups: ssoIncludedGroups ? ssoIncludedGroups.join(', ') : 'n/a',
        groups: groupsName,
        mobile: phoneNumber,
        email: eMail,
        jobRole: jobrole,
        lastSeen: seenAt,
        sso: ssoStatus,
      });
    });

    if (csvData.length === 0) {
      console.error('No data available for export.');
      return;
    }

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map((row) =>
        Object.values(row)
          .map((value) => `"${value}"`)
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'users.csv';
    link.click();
  }

  return (
    <Grid container direction="column" wrap="nowrap" justifyContent="flex-start" style={{ height: '100%' }}>
      <PageHeader
        icon={<AccessibilityRounded style={{ color: blue[900], fontSize: '3rem' }} />}
        title={'User Management'}
        onSearch={(value) => {
          tablePaginationConfig.gridProps?.onFilterModelChange({
            items: [{ field: 'fullName', operator: 'contains', value }],
          });
        }}
        searchDebounceMs={800}
        dense
        options={
          <>
            <HeaderButton
              className="active-directory"
              color="primary"
              text="Active Directory Check"
              icon={<ManageAccountsIcon fontSize="large" />}
              onClick={() => setSignupDialog(true)}
            />
            <HeaderButton
              className="add-user"
              color="primary"
              text="Add User"
              icon={<AddRounded fontSize="large" />}
              onClick={() => setNewUserDialog(true)}
            />
            <HeaderButton
              className="import-users"
              color="primary"
              text="Import Users"
              icon={<UploadFileIcon fontSize="large" />}
              onClick={() => setImportDialog(true)}
            />
            <HeaderButton
              className="import-users"
              color="primary"
              text="Export Users in CSV"
              icon={<DownloadIcon fontSize="large" />}
              onClick={downloadExcel}
            />
          </>
        }
      />

      <Grid container sx={{ mb: 1 }}>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid item>
            {options.map((option, index) => (
              <Chip
                icon={option.icon}
                style={{ marginRight: 5 }}
                label={accessCounts.find((a) => a.access === option.value)?.count || '-'}
                key={index}
                color="primary"
              />
            ))}
          </Grid>

          {!isEmpty(selected) && (
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setGroupDialog(true);
                }}
                sx={{ mr: 1 }}
              >
                Create a Group
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setGroupsDialog(true);
                }}
              >
                Add to a Group
              </Button>
            </Grid>
          )}

          {company && isTour && (
            <Tours
              id={CASES.SETUPUSERS}
              continuous
              onTourEnd={() => {
                dispatch(updateTour({ setupUsers: true }));
                setNewUserDialog(true);
              }}
            />
          )}
        </Grid>
      </Grid>

      <Grid item container style={{ flex: 1, position: 'relative' }}>
        <Grid item container style={{ position: 'absolute', height: '100%' }}>
          <DataGridPremium
            {...tablePaginationConfig?.gridProps}
            disableAggregation
            disableRowGrouping
            onRowClick={({ row }) => {
              setSelectedUser(row);
              setUserDialog(true);
            }}
            checkboxSelection={selected}
            onRowSelectionModelChange={setSelected}
            disableColumnSelector
            getRowId={(row) => row._id}
            apiRef={apiRef}
          />
        </Grid>
      </Grid>

      <UserDialog
        currentUser={currentUser}
        user={selectedUser}
        open={userDialog}
        onClose={() => setUserDialog(false)}
        onResult={(user) => {
          const users = tablePaginationConfig.getRows();
          let index = users.findIndex((x) => x._id === user._id);
          if (index === -1) return;
          users[index] = { ...users[index], ...user };
          tablePaginationConfig.updateRows(users);
          loadAccessCounts();
        }}
        onDelete={(id) => {
          tablePaginationConfig.reloadCurrentPage(true);
          loadAccessCounts();
        }}
      />
      <NewUserDialog
        currentUser={currentUser}
        user={selectedUser}
        open={newUserDialog}
        onClose={() => setNewUserDialog(false)}
        onResult={() => {
          tablePaginationConfig.reloadCurrentPage(true);
          loadAccessCounts();
        }}
      />
      <SignUpLinkDialog
        open={signupDialog}
        onClose={() => setSignupDialog(false)}
        initial={company}
        onResult={() => {
          setSignupDialog(false);
          dispatch(getCompany());
        }}
      />
      <ImportUsersDialog
        open={importDialog}
        onClose={() => setImportDialog(false)}
        onResult={() => {
          tablePaginationConfig.reloadCurrentPage();
          setImportDialog(false);
          loadAccessCounts();
        }}
      />
      <NewGroupDialog
        open={groupDialog}
        onClose={() => setGroupDialog(false)}
        onResult={() => {
          setSelected([]);
          tablePaginationConfig.reloadCurrentPage();
        }}
        initial={selected}
      />
      <GroupsDialog
        open={groupsDialog}
        onClose={() => setGroupsDialog(false)}
        onResult={() => {
          setSelected([]);
          tablePaginationConfig.reloadCurrentPage();
        }}
        initial={selected}
      />
    </Grid>
  );
}

function UserDialog({ currentUser, user, open, onClose, onResult, onDelete }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suspended, setSuspended] = useState(false);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [access, setAccess] = useState('standard');
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supportUser, setSupportUser] = useState(false);
  const role = user ? options.find((x) => x.value === user.access) : null;

  const [paidUsersInfo, setPaidUsersInfo] = useState(null);
  const [requestingMore, setRequestingMore] = useState(false);
  const [requestedDialog, setRequestedDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [withTimeout, setWithtimeout] = useState(false);
  const [userTimeout, setUserTimeout] = useState(2);
  const [transferDialog, setTransferDialog] = useState(false);
  const [workTransfers, setWorkTransfers] = useState([]);
  const [requestingPasswordReset, setRequestingPasswordReset] = useState(false);

  const hasEmail = user && validateEmail(user.email);

  useEffect(() => {
    if (!user) return;
    setSuspended(!!user.suspendedAt);
    setSupportUser(user.isSupportUser || false);
    if (!edit) return;

    axios
      .get('/admin/management/checkPaidUsers', { params: { userId: user._id } })
      .then((res) => {
        setPaidUsersInfo(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [edit, user]);

  useEffect(() => {
    if (!open) return;
    setEdit(false);
  }, [open]);

  useEffect(() => {
    if (!open || !user) return;

    axios
      .post('/workTransfer/getWorkTransfersForUser', { userId: user._id })
      .then(({ data }) => {
        setWorkTransfers(data);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      });
  }, [open, user]);

  function requestMoreUsers() {
    setRequestingMore(true);
    axios
      .post('/admin/management/requestMoreUsers')
      .then((res) => {
        setRequestingMore(false);
        setRequestedDialog(true);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setRequestingMore(false);
      });
  }

  requestingPasswordReset;

  function requestPasswordChange() {
    setRequestingPasswordReset(true);
    axios
      .post('/admin/management/requestPasswordChange', { userId: user._id })
      .then((res) => {
        setRequestingPasswordReset(false);
        StateManager.setSuccessAlert('Done');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setRequestingPasswordReset(false);
      });
  }

  function confirmPasswordChange() {
    StateManager.setConfirm(
      `You are about to request for user ${user.fullName} to change their password`,
      requestPasswordChange,
      'They will have to change it during their current session or the next time they log in',
    );
  }

  function editUser() {
    setEdit(true);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setMobile(user.mobile);
    setJobRole(user.jobRole);
    setAccess(user.access);
    setWithtimeout(!!user.timeout);
    setUserTimeout(user.timeout || 2);
    setSuspended(!!user.suspendedAt);
  }

  function confirmDelete() {
    StateManager.setConfirm(`You are about to delete user ${user.fullName}`, deleteUser);
  }

  function deleteUser() {
    let userId = user._id;
    setSaving(true);
    axios
      .post('/admin/management/deleteUser', { userId })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('User has been deleted');
        onDelete(userId);
        onClose();
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function confirmSuspend() {
    StateManager.setConfirm('You are about to suspend this user', suspendUser);
  }

  function suspendUser() {
    let userId = user._id;
    setSaving(true);
    axios
      .post('/admin/management/suspendUser', { userId })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('User has been suspended');
        setSuspended(true);
        onResult({ ...user, suspendedAt: new Date() });
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function confirmRestore() {
    StateManager.setConfirm('You are about to restore this user', restoreUser);
  }

  function restoreUser() {
    let userId = user._id;
    setSaving(true);
    axios
      .post('/admin/management/restoreUser', { userId })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('User has been restored');
        setSuspended(false);
        onResult({ ...user, suspendedAt: null });
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function confirmReset() {
    if (hasEmail) {
      StateManager.setConfirm(`Verification link will be emailed to ${user.email}`, sendResetPasswordLink);
    } else {
      setPasswordDialog(true);
    }
  }

  function sendResetPasswordLink() {
    setSaving(true);
    axios
      .post('/admin/management/sendResetPasswordLink', { userId: user._id })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('Link has been sent');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function confirmTransfering(user) {
    StateManager.setConfirm(
      'When you transfer responsibilities to another user, the user will gain all of the access and approval rights of the old user',
      () => pickUserForTransfering(user),
    );
  }

  function pickUserForTransfering(user) {
    StateManager.selectMultipleUsers(transferData, {
      singleSelect: true,
      access: ['admin', user?.access],
      title: 'Pick user to transfer the data',
    });
  }

  function transferData(res) {
    if (!isArray(res.users) || isEmpty(res.users)) return;
    const [newUser] = res.users;
    setSaving(true);
    axios
      .post('/admin/management/transferData', { userId: user._id, newUserId: newUser })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('Data has been transfered');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function done() {
    const body = {
      userId: user._id,
      firstName,
      lastName,
      email,
      mobile,
      jobRole,
      access,
      picture: 'none',
      timeout: withTimeout ? userTimeout : null,
    };
    setSaving(true);
    axios
      .post('/admin/management/saveUser', body)
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('User has been saved');
        onResult(res.data);
        onClose();
        setEdit(false);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {edit && <DialogTitle>Edit user</DialogTitle>}

      <DialogContent sx={{ mt: 2 }}>
        {edit ? (
          <Grid container>
            <Grid container item sx={{ p: 1 }}>
              <Grid container item xs={6}>
                <TextField
                  fullWidth
                  label="First name"
                  variant="outlined"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  inputProps={{ maxLength: 64 }}
                />
              </Grid>
              <Grid container item xs={6} style={{ paddingLeft: 4 }}>
                <TextField
                  fullWidth
                  label="Last name"
                  variant="outlined"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  inputProps={{ maxLength: 64 }}
                />
              </Grid>
            </Grid>
            <Grid container item sx={{ p: 1 }}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid container item sx={{ p: 1 }}>
              <TextField
                fullWidth
                label="Mobile"
                variant="outlined"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                inputProps={{ maxLength: 24 }}
              />
            </Grid>
            <Grid container item sx={{ p: 1 }}>
              <TextField
                fullWidth
                label="Job role"
                variant="outlined"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
            {currentUser?._id !== user?._id && (
              <Grid container item sx={{ p: 1 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Access</FormLabel>
                  <RadioGroup
                    aria-label="access"
                    name="access"
                    value={access}
                    onChange={(e) => setAccess(e.target.value)}
                    row
                  >
                    {options.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={option.value}
                        label={option.message}
                        control={<Radio color="primary" />}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {paidUsersInfo?.adminExceeded && (access === 'standard' || access === 'admin') && !requestingMore && (
                  <Alert severity="error" style={{ width: '100%', marginTop: 16, borderRadius: 8 }}>
                    <AlertTitle>Admin and standard limit exceeded</AlertTitle>
                    You cannot set this access level because your admin and standard user limit is exceeded. To request
                    more users please click{' '}
                    <span
                      onClick={requestMoreUsers}
                      style={{
                        color: blue[700],
                        cursor: 'pointer',
                      }}
                    >
                      here
                    </span>
                  </Alert>
                )}

                {requestingMore && (
                  <Grid container item sx={{ p: 1 }} justifyContent="flex-end">
                    <CircularProgress color="primary" size={28} />
                  </Grid>
                )}
                <RequestedDialog open={requestedDialog} onClose={() => setRequestedDialog(false)} />
              </Grid>
            )}
            <Grid container item sx={{ p: 1 }}>
              <FormControlLabel
                label="Log user out after period of inactivity"
                onChange={(e) => setWithtimeout(e.target.checked)}
                control={<Switch checked={withTimeout} color="primary" />}
              />
              <Collapse in={withTimeout} style={{ width: '100%' }}>
                <Grid container alignItems="flex-end">
                  <Typography style={{ fontWeight: 500 }}>Set timeout:</Typography>
                  <TextField
                    style={{ width: 150, margin: '0 8px' }}
                    value={userTimeout}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value < 2) return;
                      setUserTimeout(value);
                    }}
                    label="Timeout"
                    type="number"
                  />
                  <Typography style={{ fontWeight: 500 }}>minutes</Typography>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <Grid container item alignItems="center">
              <Grid item xs={4}>
                <Avatar
                  src={suspended ? undefined : user?.avatar256}
                  style={{
                    background: suspended ? undefined : user?.avatarColor,
                    width: 140,
                    height: 140,
                    fontSize: 55,
                  }}
                >
                  {user?.avatarLetters}
                </Avatar>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="h5" color={suspended ? 'textSecondary' : undefined}>
                  {user?.fullName}
                </Typography>
              </Grid>
            </Grid>
            {suspended && (
              <Grid container item style={{ marginTop: '1rem' }}>
                <Typography variant="h6" color="textSecondary">
                  This user has been suspended
                </Typography>
              </Grid>
            )}
            {!user?.isVerified && !suspended && (
              <Grid container item style={{ marginTop: '1rem' }}>
                <Typography variant="h6" style={{ color: red[600] }}>
                  This user has not been verified yet
                </Typography>
              </Grid>
            )}

            <Grid container item style={{ marginTop: '2rem' }}>
              <List>
                <ListItem disabled={suspended}>
                  <ListItemIcon>
                    <MailOutline />
                  </ListItemIcon>
                  <ListItemText primary={user?.email} />
                </ListItem>
                {user?.mobile && (
                  <ListItem disabled={suspended}>
                    <ListItemIcon>
                      <Phone />
                    </ListItemIcon>
                    <ListItemText primary={user?.mobile} />
                  </ListItem>
                )}
                {user?.jobRole && (
                  <ListItem disabled={suspended}>
                    <ListItemIcon>
                      <WorkOutline />
                    </ListItemIcon>
                    <ListItemText primary={user?.jobRole} />
                  </ListItem>
                )}
                {role && (
                  <ListItem disabled={suspended}>
                    <ListItemIcon>{role.icon}</ListItemIcon>
                    <ListItemText primary={role.message} />
                  </ListItem>
                )}
              </List>
            </Grid>

            {!isEmpty(workTransfers) && (
              <Grid item container sx={{ my: 1 }}>
                <Grid item container>
                  <Typography variant="h6" gutterBottom>
                    Work transfers:
                  </Typography>
                </Grid>

                <Grid item container>
                  {workTransfers.map((transfer) => (
                    <WorkTransfer
                      key={transfer._id}
                      data={transfer}
                      forAdmin
                      userId={user?._id}
                      onDelete={() => setWorkTransfers(workTransfers.filter((x) => x._id !== transfer._id))}
                    />
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid
              container
              item
              style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${grey[300]}` }}
              spacing={1}
            >
              {!suspended && !supportUser && (
                <Grid item>
                  <GeneralButton onClick={editUser} startIcon={<EditRounded style={{ color: grey[600] }} />}>
                    Edit user
                  </GeneralButton>
                </Grid>
              )}

              {suspended ? (
                <>
                  <Grid item>
                    <GeneralButton
                      startIcon={<RestorePageRounded style={{ color: green[600] }} />}
                      onClick={confirmRestore}
                    >
                      Restore user
                    </GeneralButton>
                  </Grid>

                  <Grid item>
                    <GeneralButton
                      startIcon={<TransferWithinAStationRounded />}
                      onClick={() => confirmTransfering(user)}
                    >
                      Transfer user data
                    </GeneralButton>
                  </Grid>

                  <Grid item>
                    <GeneralButton
                      startIcon={<DeleteOutlineOutlined style={{ color: red[500] }} />}
                      onClick={confirmDelete}
                    >
                      Delete user
                    </GeneralButton>
                  </Grid>
                </>
              ) : !supportUser ? (
                <Grid item>
                  <GeneralButton startIcon={<HighlightOffRounded />} onClick={confirmSuspend}>
                    Suspend user
                  </GeneralButton>
                </Grid>
              ) : null}

              {!suspended && (
                <>
                  {!supportUser && (
                    <Grid item>
                      <GeneralButton
                        onClick={confirmReset}
                        startIcon={hasEmail ? <EmailRounded /> : <RotateLeftRounded />}
                      >
                        {hasEmail ? 'Email password reset link' : 'Reset password'}
                      </GeneralButton>
                    </Grid>
                  )}

                  {!supportUser && (
                    <Grid item>
                      <GeneralButton onClick={confirmPasswordChange} startIcon={<PriorityHighRounded />}>
                        Request password change
                      </GeneralButton>
                    </Grid>
                  )}

                  <Grid item>
                    <GeneralButton startIcon={<LocalShippingRounded />} onClick={() => setTransferDialog(true)}>
                      Transfer work
                    </GeneralButton>
                  </Grid>

                  <TransferWorkDialog
                    open={transferDialog}
                    forAdmin
                    onClose={() => setTransferDialog(false)}
                    onResult={(res) => setWorkTransfers([...workTransfers, res])}
                    userId={user?._id}
                  />
                </>
              )}

              <ResetPasswordDialog open={passwordDialog} onClose={() => setPasswordDialog(false)} userId={user?._id} />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <StandardDialogActions
        additionalActions={
          edit ? (
            <Button
              style={{ color: red[500], borderRadius: 8, marginRight: 'auto' }}
              onClick={() => setEdit(false)}
              startIcon={<CloseRounded />}
            >
              Cancel edit
            </Button>
          ) : null
        }
        onClose={onClose}
        saving={saving}
        onDone={done}
        hideDone={!edit}
      />
    </RoundedDialog>
  );
}

export function NewUserDialog({ open, onClose, onResult }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [access, setAccess] = useState('standard');
  const [saving, setSaving] = useState(false);
  const [paidUsersInfo, setPaidUsersInfo] = useState(null);
  const [requestingMore, setRequestingMore] = useState(false);
  const [requestedDialog, setRequestedDialog] = useState(false);

  const [type, setType] = useState('email');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!open) return;
    axios
      .get('/admin/management/checkPaidUsers')
      .then((res) => {
        setPaidUsersInfo(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [open]);

  function done() {
    const body = {
      type,
      username,
      password,
      firstName,
      lastName,
      email,
      mobile,
      jobRole,
      access,
    };
    setSaving(true);
    axios
      .post('/admin/management/addUser', body)
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('User has been created');
        onResult(res.data);
        onClose();
        setFirstName('');
        setLastName('');
        setEmail('');
        setMobile('');
        setJobRole('');
        setAccess('standard');
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setType('email');
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function requestMoreUsers() {
    setRequestingMore(true);
    axios
      .post('/admin/management/requestMoreUsers')
      .then((res) => {
        setRequestingMore(false);
        setRequestedDialog(true);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setRequestingMore(false);
      });
  }

  const hideDone =
    paidUsersInfo?.totalExceeded ||
    !firstName ||
    !lastName ||
    (type === 'email' && !email) ||
    (type === 'username' && (String(password).length < 3 || confirmPassword !== password));

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New user</DialogTitle>

      <DialogContent>
        <Grid container>
          <Grid container item sx={{ p: 1 }}>
            <Grid container item xs={6} style={{ paddingRight: 4 }}>
              <TextField
                fullWidth
                label="First name"
                variant="outlined"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
            <Grid container item xs={6} style={{ paddingLeft: 4 }}>
              <TextField
                fullWidth
                label="Last name"
                variant="outlined"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
          </Grid>
          <Grid container item sx={{ p: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">User name type</FormLabel>
              <RadioGroup aria-label="type" name="type" value={type} onChange={(e) => setType(e.target.value)} row>
                <FormControlLabel value={'email'} label={'Email'} control={<Radio color="primary" />} />
                <FormControlLabel value={'username'} label={'Username only'} control={<Radio color="primary" />} />
              </RadioGroup>
            </FormControl>
          </Grid>
          {type === 'email' && (
            <Grid container item sx={{ p: 1 }}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          )}
          {type === 'username' && (
            <>
              <Grid container item sx={{ p: 1 }}>
                <TextField
                  fullWidth
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid container item sx={{ p: 1 }}>
                <TextField
                  type="password"
                  variant="outlined"
                  fullWidth
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{
                    autocomplete: 'new-password',
                    form: {
                      autocomplete: 'off',
                    },
                  }}
                />
              </Grid>
              <Grid container item sx={{ p: 1 }}>
                <TextField
                  type="password"
                  variant="outlined"
                  error={password && confirmPassword && confirmPassword !== password}
                  helperText={
                    password && confirmPassword && confirmPassword !== password ? 'Passwords do not match' : ''
                  }
                  fullWidth
                  label="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                  }}
                  inputProps={{
                    autocomplete: 'new-password',
                    form: {
                      autocomplete: 'off',
                    },
                  }}
                />
              </Grid>
            </>
          )}

          <Grid container item sx={{ p: 1 }}>
            <TextField
              fullWidth
              label="Mobile"
              variant="outlined"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              inputProps={{ maxLength: 24 }}
            />
          </Grid>
          <Grid container item sx={{ p: 1 }}>
            <TextField
              fullWidth
              label="Job role"
              variant="outlined"
              value={jobRole}
              onChange={(e) => setJobRole(e.target.value)}
              inputProps={{ maxLength: 64 }}
            />
          </Grid>
          <Grid container item sx={{ p: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Access</FormLabel>
              <RadioGroup
                aria-label="access"
                name="access"
                value={access}
                onChange={(e) => setAccess(e.target.value)}
                row
              >
                {options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option.value}
                    label={option.message}
                    control={<Radio color="primary" />}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          {paidUsersInfo?.adminExceeded && (access === 'standard' || access === 'admin') && (
            <Alert severity="error" style={{ width: '100%', marginTop: 16, borderRadius: 8 }}>
              <AlertTitle>Admin and standard limit exceeded</AlertTitle>
              You cannot set this access level because your admin and standard user limit is exceeded. To request more
              users please click{' '}
              <span
                onClick={requestMoreUsers}
                style={{
                  color: blue[700],
                  cursor: 'pointer',
                }}
              >
                here
              </span>
            </Alert>
          )}
          {paidUsersInfo?.totalExceeded && (
            <Alert severity="error" style={{ width: '100%', marginTop: 16, borderRadius: 8 }}>
              <AlertTitle>User limit exceeded</AlertTitle>
              You cannot create this user because your user limit is exceeded. To request more users please click{' '}
              <span
                onClick={requestMoreUsers}
                style={{
                  color: blue[700],
                  cursor: 'pointer',
                }}
              >
                here
              </span>
            </Alert>
          )}
          {requestingMore && (
            <Grid container item sx={{ p: 1 }} justifyContent="flex-end">
              <CircularProgress color="primary" size={28} />
            </Grid>
          )}
          <RequestedDialog open={requestedDialog} onClose={() => setRequestedDialog(false)} />
        </Grid>
      </DialogContent>

      <StandardDialogActions
        onClose={onClose}
        closeButtonText="CANCEL"
        saving={saving}
        onDone={done}
        hideDone={hideDone}
      />
    </RoundedDialog>
  );
}

function RequestedDialog({ open, onClose }) {
  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Thank you for your request</DialogTitle>
      <DialogContent>
        <Typography>One of our agents will be in touch with you soon</Typography>
      </DialogContent>
      <StandardDialogActions onClose={onClose} />
    </RoundedDialog>
  );
}

function ResetPasswordDialog({ open, onClose, userId }) {
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
  }, [open]);

  function done() {
    setSaving(true);
    axios
      .post('/admin/management/resetPassword', { newPassword, userId })
      .then((res) => {
        StateManager.setSuccessAlert('Password has been changed');
        setSaving(false);
        onClose();
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Grid container item>
          <Alert severity="info" style={{ width: '100%', marginBottom: 16, borderRadius: 8 }}>
            <AlertTitle>To ensure the security of the account your password must:</AlertTitle>
            <Typography style={{ fontSize: 14 }}>Be at least 8 characters long</Typography>
            <Typography style={{ fontSize: 14 }}>Contain at least 1 lowercase letter</Typography>
            <Typography style={{ fontSize: 14 }}>Contain at least 1 uppercase letter</Typography>
            <Typography style={{ fontSize: 14 }}>Contain at least 1 special character</Typography>
            <Typography sx={{ mt: 1, fontWeight: 500 }}>
              The user will have to change it the first time they log in with the new password
            </Typography>
          </Alert>
        </Grid>
        <TextField
          required
          fullWidth
          label="New password"
          type="password"
          variant="outlined"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          style={{ marginBottom: 16 }}
          inputProps={{
            autocomplete: 'new-password',
          }}
          id={'new-password'}
        />
        <TextField
          required
          fullWidth
          error={newPassword && newPassword !== confirmPassword}
          helperText={newPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
          label="Confirm new password"
          type="password"
          variant="outlined"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          inputProps={{
            autocomplete: 'new-password',
          }}
          id={'new-password-confirm'}
        />
      </DialogContent>
      <StandardDialogActions
        saving={saving}
        onClose={onClose}
        closeButtonText="CANCEL"
        hideDone={!newPassword || !confirmPassword || newPassword !== confirmPassword}
        onDone={done}
      />
    </RoundedDialog>
  );
}

function SignUpLinkDialog({ open, onClose, onResult, initial }) {
  const [loadingLink, setLoadingLink] = useState(false);
  const [link, setLink] = useState(false);
  const [linkExpires, setLinkExpires] = useState(false);
  const [adSettings, setAdSettings] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [saving, setSaving] = useState(false);

  const expired = linkExpires ? moment(linkExpires).isBefore(moment()) : false;

  function generateSignupLink() {
    setLoadingLink(true);
    axios
      .post('/admin/management/generateSignupLink')
      .then((res) => {
        setLoadingLink(false);
        setLink(res.data.link);
        setLinkExpires(res.data.expires);
        if (!navigator.clipboard) {
          return;
        }
        navigator.clipboard.writeText(res.data.link).then(
          () => {
            StateManager.setSuccessAlert('Link copied to clipboard');
          },
          (err) => {
            console.error('Async: Could not copy text: ', err);
          },
        );
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setLoadingLink(false);
      });
  }

  function onSave() {
    setSaving(true);
    axios
      .post('/admin/management/adSettings', { tenantId, adSettings })
      .then((res) => {
        setSaving(false);
        setAdSettings(res.data.adSettings);
        setTenantId(res.data.tenantId);
        StateManager.setSuccessAlert('User Authentication Settings Updated');
        onResult();
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  useEffect(() => {
    if (!open) return;
    setAdSettings(initial.adSettings || false);
    setTenantId(initial.tenantId || '');
    axios
      .get('/admin/management/getSignupLink')
      .then((res) => {
        setLoadingLink(false);
        setLink(res.data.link);
        setLinkExpires(res.data.expires);
      })
      .catch((err) => StateManager.setAxiosErrorAlert(err));
  }, [open, initial]);

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>User Authentication Settings</DialogTitle>
      <DialogContent>
        <Grid>
          <Grid container item sx={{ p: 1 }}>
            <FormControlLabel
              label="AD Settings"
              onChange={(e) => setAdSettings(e.target.checked)}
              control={<Switch checked={adSettings} color="primary" />}
            />
          </Grid>
          {adSettings && (
            <TextField
              required
              fullWidth
              label="Tenant ID"
              variant="outlined"
              value={tenantId}
              helperText=""
              onChange={(event) => setTenantId(event.target.value)}
            />
          )}
          <Grid container item>
            <Alert severity="warning" sx={{ width: '100%', my: 2, borderRadius: 1 }}>
              {adSettings ? (
                <Typography>
                  When AD setting is turned on, users will only be able to use SSO to join. This setting will only allow
                  users to join if they are part of your organisation’s AD.
                </Typography>
              ) : (
                <Typography>Turn this setting on to link your organisation AD to authenticate your users</Typography>
              )}
            </Alert>
          </Grid>
          <Grid container>
            <GeneralButton onClick={generateSignupLink} startIcon={<HowToRegIcon sx={{ color: blue[600] }} />}>
              Generate new link
            </GeneralButton>
          </Grid>
          {loadingLink && (
            <Grid container item style={{ marginTop: 16 }}>
              <CircularProgress size={20} color="primary" style={{ margin: 5 }} />
            </Grid>
          )}
          {link && (
            <Grid container item style={{ marginTop: 16 }}>
              <Grid container item style={{ marginBottom: 16 }}>
                {expired ? (
                  <Typography color="error">
                    Link expired at {moment(linkExpires).format('DD/MM/YYYY hh:mm a')}
                  </Typography>
                ) : (
                  <Typography>Link expires at {moment(linkExpires).format('DD/MM/YYYY hh:mm a')}</Typography>
                )}
              </Grid>
              <Grid container item>
                <TextField fullWidth label="link" variant="outlined" value={link} disabled={expired} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={onSave} hideDone={adSettings && !tenantId} saving={saving} />
    </RoundedDialog>
  );
}
